import { AddShoppingCartOutlined } from '@mui/icons-material';
import { Box, Button, Stack } from '@mui/material';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { MarkdownConverter } from '@synergy/components-next';
import { PaloPositionColumn, PaloPositionImage, PaloPositionTable } from '@synergy/components-palo';
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { getLocalizedString } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import {
    MemberPurchaseOrder,
    MemberPurchaseOrderDetail,
    MemberPurchaseOrderItem,
} from '../models/member-purchase-order';
import { PaloOrderInvoices } from './palo-order-invoices';
import { localesSelector } from '../../../common/selectors/locale-selectors';
import { useAddToShopCart } from './use-add-to-shop-cart';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

const Fact = ({ label, children, wide }: { label: ReactNode; children: ReactNode; wide?: boolean }): ReactElement => (
    <Stack sx={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
        <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>{label}</Box>
        <Box sx={{ fontSize: '12.5px', whiteSpace: 'pre-line' }}>{children || '–'}</Box>
    </Stack>
);

const addressLines = (address?: MemberPurchaseOrderDetail['invoiceAddress']): string | undefined =>
    address
        ? [
              address.title,
              [address.street, address.number].filter(Boolean).join(' '),
              [address.zip, address.city].filter(Boolean).join(' '),
          ]
              .filter(Boolean)
              .join('\n')
        : undefined;

/**
 * The opened row of a member's order: its positions, and what the card in the
 * shop told about the order (#2279).
 */
export const PaloOrderDetails = ({ order }: { order: MemberPurchaseOrder }): ReactElement => {
    const dispatch = useThunkDispatch();
    const locales = useSelector(localesSelector);
    const [detail, setDetail] = useState<MemberPurchaseOrderDetail>();
    const { adding, add } = useAddToShopCart();

    useEffect(() => {
        let cancelled = false;

        void dispatch(async (_dispatch, _getState, { api }) => {
            const details = await api.orders.get(order.id);

            if (!cancelled) {
                setDetail(details.success ? details.result : undefined);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [dispatch, order.id]);

    const columns = useMemo<PaloPositionColumn<MemberPurchaseOrderItem>[]>(
        () => [
            {
                key: 'image',
                type: 'icon',
                label: <FormattedMessage id="app.orders.position.image" />,
                render: item => (
                    <PaloPositionImage
                        image={item.previewImage}
                        name={getLocalizedString(locales, item.name)}
                        caption={
                            item.number && (
                                <>
                                    <FormattedMessage id="app.orders.position.number" /> {item.number}
                                </>
                            )
                        }
                    />
                ),
            },
            {
                key: 'number',
                type: 'articleNumber',
                label: <FormattedMessage id="app.orders.position.number" />,
                render: item => item.number ?? '–',
            },
            {
                key: 'name',
                type: 'name',
                label: <FormattedMessage id="app.orders.position.article" />,
                render: item => getLocalizedString(locales, item.name),
            },
            {
                key: 'gtin',
                type: 'articleNumber',
                label: <FormattedMessage id="app.orders.position.gtin" />,
                render: item => item.globalTradeItemNumber ?? '–',
            },
            {
                key: 'quantity',
                type: 'quantity',
                label: <FormattedMessage id="app.orders.position.quantity" />,
                render: item => (
                    <>
                        <FormattedNumber value={item.quantity} /> {item.orderUnit}
                    </>
                ),
            },
            {
                key: 'price',
                type: 'price',
                label: <FormattedMessage id="app.orders.position.price" />,
                render: item => <FormattedNumber value={item.netPrice} minimumFractionDigits={2} />,
            },
            {
                key: 'total',
                type: 'amount',
                label: <FormattedMessage id="app.orders.position.total" />,
                render: item => <FormattedNumber value={item.totalNetPrice} minimumFractionDigits={2} />,
            },
        ],
        [locales]
    );

    if (!detail) {
        return (
            <Box sx={{ fontSize: '12px', color: tokens.color.ink3 }}>
                <FormattedMessage id="app.orders.detail.loading" />
            </Box>
        );
    }

    const money = (value: number): ReactNode => (
        <FormattedNumber value={value} style="currency" currency={detail.currency ?? order.currency} />
    );

    return (
        <Stack gap="12px">
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: '10px 20px',
                }}
            >
                <Fact label={<FormattedMessage id="app.orders.detail.aviso" />}>
                    {detail.customerAvisoContact?.fullName}
                </Fact>
                <Fact label={<FormattedMessage id="app.orders.detail.invoiceAddress" />}>
                    {addressLines(detail.invoiceAddress)}
                </Fact>
                <Fact label={<FormattedMessage id="app.orders.detail.orderReference" />}>{detail.orderReference}</Fact>
                <Fact label={<FormattedMessage id="app.orders.detail.costs" />}>
                    <FormattedMessage
                        id="app.orders.detail.costs.value"
                        values={{ shipping: money(detail.netShippingCosts), surcharge: money(detail.netSurcharge) }}
                    />
                </Fact>

                <Fact label={<FormattedMessage id="app.orders.detail.invoice" />}>
                    <PaloOrderInvoices order={order} />
                </Fact>
                {detail.noteToSupplier && (
                    // Written formatted, so read formatted: "**Lieferung bis …**" stood
                    // there with its stars (R-153).
                    <Fact label={<FormattedMessage id="app.orders.noteToSupplier" />}>
                        <MarkdownConverter value={detail.noteToSupplier} />
                    </Fact>
                )}
                <Fact wide={true} label={<FormattedMessage id="app.orders.detail.conditions" />}>
                    {[detail.termsOfDeliveryContent, getLocalizedString(locales, detail.paymentTerm?.text)]
                        .filter(Boolean)
                        .join('\n')}
                </Fact>
            </Box>

            {/* The opened row is as wide as the list; the positions never widen it. */}
            <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <PaloPositionTable<MemberPurchaseOrderItem>
                    items={detail.items}
                    columns={columns}
                    getRowKey={item => item.id}
                    readonly={true}
                    quantityOf={item => item.quantity}
                    amountOf={item => item.totalNetPrice}
                    currency={detail.currency ?? order.currency}
                    rowActions={item =>
                        null !== item.productId
                            ? [
                                  {
                                      label: <FormattedMessage id="app.orders.addToCart" />,
                                      icon: <AddShoppingCartOutlined fontSize="small" />,
                                      onClick: () => void add([item]),
                                      disabled: !item.isAvailableForPurchase,
                                      reason: <FormattedMessage id="app.orders.notBuyableHint" />,
                                  },
                              ]
                            : []
                    }
                />
            </Box>

            <Box>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={
                        adding || !detail.items.some(item => null !== item.productId && item.isAvailableForPurchase)
                    }
                    onClick={() => void add(detail.items)}
                >
                    <FormattedMessage id="app.orders.addAllToCart" />
                </Button>
            </Box>
        </Stack>
    );
};
